import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import { updateMetadataComparison, applyFileRename, removeComparisonRow, dismissEditTip } from '../redux/slices/fixMetadataSlice';
import { addTrack } from '../redux/slices/musicPlayerSlice';
import { track as trackEvent } from '../utils/analytics';
import RowActions from './RowActions';
import MetadataSubTabs from './MetadataSubTabs';
import { ReactComponent as TipIcon } from '../assets/icons/tip.svg';
import { useDesignV2 } from '../utils/designV2';
import './MetadataComparison.scss';

function getSuggestion(item, mode) {
    if (mode === 'none') return null;
    if (item.filenameByMode) return item.filenameByMode[mode] || null;
    return item.suggestedFilename || null;
}

const FilenameComparison = React.forwardRef(({ activeSubTab, onSubTabChange, onSelectionChange }, ref) => {
    const dispatch = useDispatch();
    const { metadataComparisons, filenameMode, hideEditTip } = useSelector((state) => state.fixMetadata);
    const mode = filenameMode || 'both';
    // DESIGN V2: no toolbar (tabs live in the tool header); selection count +
    // Select all sit in the table header row.
    const v2 = useDesignV2();

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [editingPath, setEditingPath] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [manualEdits, setManualEdits] = useState({});
    const editInputRef = useRef(null);
    const [selectedPaths, setSelectedPaths] = useState(new Set());
    // Right-click opens the row's shared TrackContextMenu at the cursor —
    // identical options to the ⋯ button (same parity as the Tags sub-tab).
    const [rowCtx, setRowCtx] = useState(null); // { key, x, y, nonce }
    const ctxNonceRef = useRef(0);

    // Derive suggestions based on current mode + manual edits
    // Placeholder rows the analyzer has not reached yet (item.pending) carry
    // no suggestion and would read as "already clean" here — leave them to
    // the Tags table and let this list fill in as results land.
    const items = useMemo(() =>
        metadataComparisons.filter(item => !item.pending).map(item => ({
            ...item,
            suggestion: manualEdits[item.filePath] !== undefined
                ? manualEdits[item.filePath]
                : getSuggestion(item, mode),
        })),
        [metadataComparisons, mode, manualEdits]
    );

    const suggestionsCount = items.filter(item => item.suggestion).length;

    // Auto-select files with suggestions when mode or data changes
    useEffect(() => {
        const paths = new Set();
        for (const item of items) {
            if (item.suggestion) paths.add(item.filePath);
        }
        setSelectedPaths(paths);
    }, [items]);

    // Notify parent of actionable selection count
    useEffect(() => {
        if (onSelectionChange) {
            const count = [...selectedPaths].filter(p =>
                items.some(item => item.filePath === p && item.suggestion)
            ).length;
            onSelectionChange(count);
        }
    }, [selectedPaths, items]);

    useEffect(() => {
        if (editingPath && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingPath]);

    // Expose selected files for the rename handler
    React.useImperativeHandle(ref, () => ({
        getSelectedFiles: () =>
            items
                .filter(item => selectedPaths.has(item.filePath) && item.suggestion)
                .map(item => ({ ...item, suggestedFilename: item.suggestion }))
    }), [items, selectedPaths]);

    const filteredData = items;

    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const aVal = sortConfig.key === 'fileName' ? a.fileName : (a.suggestion || '');
        const bVal = sortConfig.key === 'fileName' ? b.fileName : (b.suggestion || '');
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const toggleSelection = (filePath) => {
        setSelectedPaths(prev => {
            const next = new Set(prev);
            if (next.has(filePath)) next.delete(filePath);
            else next.add(filePath);
            return next;
        });
    };

    const handleRowClick = (item) => {
        const ext = item.fileName.match(/\.\w+$/)?.[0] || '';
        const currentStem = item.suggestion
            ? item.suggestion.slice(0, -ext.length || undefined)
            : item.fileName.slice(0, -ext.length || undefined);
        setEditingPath(item.filePath);
        setEditValue(currentStem);
    };

    const handleEditSave = async (item) => {
        if (!editingPath) return; // Prevent double-fire from blur after Enter
        const ext = item.fileName.match(/\.\w+$/)?.[0] || '';
        const newName = editValue.trim() + ext;
        setEditingPath(null);
        if (newName && newName !== item.fileName) {
            let handled = false;
            window.electron.renameFiles(
                { files: [{ filePath: item.filePath, fileName: item.fileName, suggestedFilename: newName, current: item.current, database: item.database, metadataSource: item.metadataSource }] },
                (progress) => {
                    if (progress.results && !handled) {
                        handled = true;
                        for (const result of progress.results) {
                            if (result.success && result.newPath) {
                                dispatch(applyFileRename({
                                    filePath: result.filePath,
                                    newPath: result.newPath,
                                    newName: result.newName
                                }));
                                setManualEdits(prev => {
                                    const next = { ...prev };
                                    delete next[result.filePath];
                                    return next;
                                });
                            }
                        }
                    }
                }
            );
        }
    };

    const handleEditKeyDown = (e, item) => {
        if (e.key === 'Enter') handleEditSave(item);
        else if (e.key === 'Escape') setEditingPath(null);
    };

    // ---- Row menu (⋯ + right-click), same shared menu as every track tab ----
    // Items come from the same metadataComparisons store as the Tags sub-tab,
    // so the analyzed (database/Mixpedia) identity is available here too.
    const menuTrackFor = (item) => ({
        title: item.database?.title || item.current?.title || (item.fileName || '').replace(/\.[^/.]+$/, ''),
        artist: item.database?.artist || item.current?.artist || '',
        filePath: item.filePath,
        artwork: item.database?.coverUrl || null,
        djpalId: item.database?.djpalId || null,
    });

    const handleAddToQueue = async (item) => {
        try {
            const t = menuTrackFor(item);
            const musicSrc = await window.electron.getAudioUrl(item.filePath);
            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(item.filePath);
            } catch (err) {
            }
            dispatch(addTrack({
                name: t.title || 'Unknown Track',
                singer: t.artist || 'Unknown Artist',
                cover: t.artwork || albumArtUrl,
                musicSrc,
            }));
        } catch (error) {
            console.error('Error adding track to queue:', error);
        }
    };

    const removeRowFromList = (filePath) => {
        dispatch(removeComparisonRow(filePath));
        trackEvent('tracks_removed_from_list', { tab: 'fix_metadata', count: 1, via: 'menu' });
    };

    const handleRowContextMenu = (item, e) => {
        e.preventDefault();
        setRowCtx({ key: item.filePath, x: e.clientX, y: e.clientY, nonce: ++ctxNonceRef.current });
    };

    const renderRowActions = (item) => (
        <span onClick={(e) => e.stopPropagation()}>
            <RowActions
                track={menuTrackFor(item)}
                onAddToQueue={() => handleAddToQueue(item)}
                onRemoveFromLibrary={() => removeRowFromList(item.filePath)}
                removeLabel="Remove from list"
                openAtPoint={rowCtx && rowCtx.key === item.filePath ? rowCtx : null}
                desktopLike
                analyticsFrom="fix_metadata"
            />
        </span>
    );

    if (metadataComparisons.length === 0) return null;

    const selectedCount = [...selectedPaths].filter(p =>
        items.some(item => item.filePath === p && item.suggestion)
    ).length;

    return (
        <>
            <div className="file-list-container">
                {/* Same single-toolbar layout as the Tags sub-tab. */}
                {!v2 && <div className="metadata-toolbar">
                    {activeSubTab && onSubTabChange && <MetadataSubTabs activeSubTab={activeSubTab} onSubTabChange={onSubTabChange} />}
                    {!hideEditTip && (
                        <span className="metadata-toolbar-tip">
                            <TipIcon className="tip-icon" />
                            <span className="tip-text">Tip: Click any row to edit the filename</span>
                            <button className="tip-close-button" aria-label="Dismiss tip" onClick={() => dispatch(dismissEditTip())}>×</button>
                        </span>
                    )}
                    <div className="metadata-toolbar-selection">
                        <span className="metadata-selection-count">{suggestionsCount} of {items.length} need renaming</span>
                        <div className="button-group">
                            <Button variant="outline-secondary" size="sm" onClick={() => setSelectedPaths(new Set(items.map(i => i.filePath)))}>
                                Select All
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => setSelectedPaths(new Set())}>
                                Deselect All
                            </Button>
                        </div>
                    </div>
                </div>}
                <div className="metadata-comparison">
                    <div className="comparison-table-container">
                        <table className="comparison-table">
                            <thead>
                                <tr>
                                    <th className="checkbox-col" style={{ width: '35px' }}>
                                        {v2 && (
                                            <input
                                                type="checkbox"
                                                aria-label="Select all"
                                                title={selectedPaths.size === items.length ? 'Deselect all' : 'Select all'}
                                                checked={items.length > 0 && selectedPaths.size === items.length}
                                                ref={(el) => { if (el) el.indeterminate = selectedPaths.size > 0 && selectedPaths.size < items.length; }}
                                                onChange={() => setSelectedPaths(selectedPaths.size === items.length ? new Set() : new Set(items.map(i => i.filePath)))}
                                            />
                                        )}
                                    </th>
                                    <th style={{ cursor: 'pointer', position: 'relative' }} onClick={() => handleSort('fileName')}>
                                        Filename
                                        {v2 && <span className="v2-th-note" onClick={(e) => e.stopPropagation()}>· {suggestionsCount} of {items.length} need renaming · {selectedCount} selected</span>}
                                        {sortConfig.key === 'fileName' && (
                                            <span className="sort-indicator">{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((item) => (
                                    <tr
                                        key={item.filePath}
                                        className={selectedPaths.has(item.filePath) ? 'selected' : ''}
                                        onClick={() => handleRowClick(item)}
                                        onContextMenu={(e) => handleRowContextMenu(item, e)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedPaths.has(item.filePath)}
                                                onChange={() => toggleSelection(item.filePath)}
                                            />
                                        </td>
                                        <td title={item.filePath}>
                                            {editingPath === item.filePath ? (
                                                <div className="metadata-cell" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        ref={editInputRef}
                                                        className="filename-edit-input"
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onBlur={() => handleEditSave(item)}
                                                        onKeyDown={(e) => handleEditKeyDown(e, item)}
                                                    />
                                                    <span className="filename-edit-ext">{item.fileName.match(/\.\w+$/)?.[0] || ''}</span>
                                                </div>
                                            ) : (
                                                <div className="metadata-cell" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <div className={`current-value ${item.suggestion ? 'changed' : ''}`}>
                                                        {item.fileName}
                                                        {!item.suggestion && <span className="match-indicator" title="Filename is already clean">✓</span>}
                                                    </div>
                                                    {item.suggestion && (
                                                        <>
                                                            <div className="arrow">→</div>
                                                            <div className="new-value">{item.suggestion}</div>
                                                        </>
                                                    )}
                                                    <span style={{ marginLeft: 'auto' }}>{renderRowActions(item)}</span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
});

export default FilenameComparison;
